import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { useEffect } from 'react'
import {
  Button,
  Card,
  CardActions,
  CardContent,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { FaSave } from 'react-icons/fa'
import Header from 'components/Header'
import { useBasePath, useSession } from 'components/Providers'
import { save } from 'store/settings/marking-period'
import dayjs from 'dayjs'

export default function MarkingPeriodEditForm({ markingPeriod }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const basePath = useBasePath()
  const { academicYear } = useSession()
  const { t } = useTranslation()

  const { control, handleSubmit, reset, formState } = useForm({
    defaultValues: { readOnly: markingPeriod?.readOnly ?? false },
  })

  useEffect(() => {
    reset({ readOnly: markingPeriod?.readOnly ?? false })
  }, [markingPeriod, reset])

  const onSubmit = async data => {
    const res = await dispatch(
      save({ academicYearId: academicYear.id, id: markingPeriod.id, readOnly: data.readOnly }),
    )
    if (res.payload) {
      navigate(basePath)
    }
  }

  return (
    <Card
      data-component="MarkingPeriodEditForm"
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      sx={{ minHeight: '100%' }}
    >
      <Header title={t('Edit Marking Period')} small close="." borderBottom />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label={t('Name')}
              value={markingPeriod.name ?? ''}
              InputProps={{ readOnly: true }}
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label={t('Start Date')}
              value={dayjs(markingPeriod.startDate).format('MM/DD/YYYY')}
              InputProps={{ readOnly: true }}
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label={t('End Date')}
              value={dayjs(markingPeriod.endDate).format('MM/DD/YYYY')}
              InputProps={{ readOnly: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="readOnly"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                  }
                  label={t('Read Only')}
                />
              )}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {t('When enabled, grades for this marking period are locked and cannot be changed.')}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', mr: 1 }}>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          disabled={!formState.isDirty}
          startIcon={<FaSave />}
        >
          {t('SAVE')}
        </Button>
      </CardActions>
    </Card>
  )
}
